import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, getJson } from './client'
import type {
  Barrier,
  Control,
  Drift,
  FileDoc,
  Indices,
  Mapping,
  Module,
  Narrative,
  Obligation,
  ObligationCreate,
  ObligationUpdate,
  Question,
  ResponseIn,
  Site,
  SiteCreate,
  Source,
  WithdrawImpact,
  WithdrawnCitation,
} from './types'

export const qk = {
  sites: ['sites'] as const,
  site: (id: string) => ['site', id] as const,
  indices: (id: string) => ['site', id, 'indices'] as const,
  barriers: (id: string) => ['site', id, 'barriers'] as const,
  modules: (id: string) => ['site', id, 'modules'] as const,
  controls: (id: string) => ['site', id, 'controls'] as const,
  drift: (id: string) => ['site', id, 'drift'] as const,
  narrative: (id: string) => ['site', id, 'narrative'] as const,
  files: (id: string) => ['site', id, 'files'] as const,
  mappings: (id: string) => ['site', id, 'mappings'] as const,
  obligations: (id: string) => ['site', id, 'obligations'] as const,
  withdrawn: (id: string) => ['site', id, 'withdrawn'] as const,
  questions: ['questions'] as const,
}

const derivedKeys = (id: string) => [qk.indices, qk.barriers, qk.modules, qk.controls, qk.drift, qk.narrative].map((k) => k(id))

export type Me = { oid: string; email?: string | null; name?: string | null }

/**
 * Current user. GET /me onboards on first sign-in (the backend clones the demo
 * templates into the user's private workspace), so this is gated by RequireAuth
 * before any site data loads. `enabled` guards it to authenticated sessions only.
 */
export const useMe = (enabled = true) =>
  useQuery({ queryKey: ['me'], queryFn: () => getJson<Me>('/me'), enabled, retry: 1, staleTime: Infinity })

export const useSites = () => useQuery({ queryKey: qk.sites, queryFn: () => getJson<Site[]>('/sites') })

export function useCreateSite() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: SiteCreate) => api.post('/sites', body).then((r) => r.data as { site_id: string }),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.sites }),
  })
}

/** Delete a site and all its data (cascades server-side). */
export function useDeleteSite() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (siteId: string) => api.delete(`/sites/${siteId}`).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.sites }),
  })
}

export type FleetRow = {
  id: string
  name: string
  pack: 'BESS' | 'DSEAR'
  indices?: Indices
  degraded: boolean
}

/** All sites + their indices (parallel; petrochem 404 -> degraded). */
export function useFleet(): { rows: FleetRow[]; isLoading: boolean } {
  const sites = useSites()
  const list = sites.data ?? []
  const results = useQueries({
    queries: list.map((s) => ({
      queryKey: qk.indices(s.site_id),
      queryFn: () => getJson<Indices>(`/sites/${s.site_id}/indices`),
      retry: false,
      enabled: !!s.site_id,
    })),
  })
  const rows: FleetRow[] = list.map((s, i) => ({
    id: s.site_id,
    name: s.site_name,
    pack: s.industry === 'petrochem' ? 'DSEAR' : 'BESS',
    indices: results[i]?.data,
    degraded: results[i]?.isError ?? false,
  }))
  return { rows, isLoading: sites.isLoading || results.some((r) => r.isLoading) }
}

export const useSite = (id: string) =>
  useQuery({ queryKey: qk.site(id), queryFn: () => getJson<Site>(`/sites/${id}`), enabled: !!id })

export const useIndices = (id: string) =>
  useQuery({ queryKey: qk.indices(id), queryFn: () => getJson<Indices>(`/sites/${id}/indices`), enabled: !!id })

// /barriers and /modules return an object keyed by code (B0…B9 / M1…M6).
export const useBarriers = (id: string) =>
  useQuery({
    queryKey: qk.barriers(id),
    queryFn: () => getJson<Record<string, Barrier>>(`/sites/${id}/barriers`),
    enabled: !!id,
  })

export const useModules = (id: string) =>
  useQuery({
    queryKey: qk.modules(id),
    queryFn: () => getJson<Record<string, Module>>(`/sites/${id}/modules`),
    enabled: !!id,
  })

export const useDrift = (id: string) =>
  useQuery({ queryKey: qk.drift(id), queryFn: () => getJson<Drift>(`/sites/${id}/drift`), enabled: !!id })

export const useNarrative = (id: string) =>
  useQuery({ queryKey: qk.narrative(id), queryFn: () => getJson<Narrative>(`/sites/${id}/narrative`), enabled: !!id })

export const useControls = (id: string) =>
  useQuery({ queryKey: qk.controls(id), queryFn: () => getJson<Control[]>(`/sites/${id}/controls`), enabled: !!id })

/** Re-run the engine (POST an empty responses list triggers a recompute). */
export function useRecompute(siteId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => api.post(`/sites/${siteId}/responses`, []).then((r) => r.data),
    onSuccess: () => {
      for (const key of derivedKeys(siteId)) qc.invalidateQueries({ queryKey: key })
    },
  })
}

export const useQuestions = () =>
  useQuery({ queryKey: qk.questions, queryFn: () => getJson<Question[]>('/questions') })

/** A file is still moving through the pipeline until it's mapped or failed. */
const FILE_TERMINAL = new Set(['mapped', 'failed'])

/** Files for a site; auto-polls (1.5s) while any document is still processing. */
export const useFiles = (id: string) =>
  useQuery({
    queryKey: qk.files(id),
    queryFn: () => getJson<FileDoc[]>(`/sites/${id}/files`),
    enabled: !!id,
    refetchInterval: (query) => {
      const files = query.state.data as FileDoc[] | undefined
      const active = files?.some((f) => !FILE_TERMINAL.has(f.processing_status))
      return active ? 1500 : false
    },
  })

export const useMappings = (id: string) =>
  useQuery({ queryKey: qk.mappings(id), queryFn: () => getJson<Mapping[]>(`/sites/${id}/mappings`), enabled: !!id })

/** A document's extracted text + citation highlight spans (view-in-doc). */
export const useSource = (fileId?: string) =>
  useQuery({
    queryKey: ['source', fileId],
    queryFn: () => getJson<Source>(`/files/${fileId}/source`),
    enabled: !!fileId,
  })

/** Dry-run: what withdrawing a document would do (supported/reverting answers). */
export const useDeleteImpact = (fileId?: string) =>
  useQuery({
    queryKey: ['delete-impact', fileId],
    queryFn: () => getJson<WithdrawImpact>(`/files/${fileId}/delete-impact`),
    enabled: !!fileId,
  })

/** Answers whose supporting document was withdrawn (provenance for the Assessment). */
export const useWithdrawn = (id: string) =>
  useQuery({ queryKey: qk.withdrawn(id), queryFn: () => getJson<WithdrawnCitation[]>(`/sites/${id}/withdrawn`), enabled: !!id })

/** Withdraw (soft-delete) a document; reverts sole-supported answers + recomputes. */
export function useDeleteFile(siteId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (fileId: string) => api.delete(`/files/${fileId}`).then((r) => r.data as WithdrawImpact),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.files(siteId) })
      qc.invalidateQueries({ queryKey: qk.mappings(siteId) })
      qc.invalidateQueries({ queryKey: qk.withdrawn(siteId) })
      for (const key of derivedKeys(siteId)) qc.invalidateQueries({ queryKey: key })
    },
  })
}

export function useUploadFile(siteId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (file: File) => {
      const fd = new FormData()
      fd.append('file', file)
      return api
        .post(`/sites/${siteId}/files`, fd, { headers: { 'Content-Type': 'multipart/form-data' } })
        .then((r) => r.data)
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.files(siteId) }),
  })
}

/** Extract → chunk → embed → classify (calls Bedrock; blocks until done). */
export function useProcessFile(siteId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (fileId: string) => api.post(`/files/${fileId}/process`).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.files(siteId) }),
  })
}

/** Run AI mapping (Nova) of processed evidence against the given questions. */
export function useMapEvidence(siteId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (questionIds: string[]) =>
      api.post(`/sites/${siteId}/map`, { question_ids: questionIds, auto_answer: true }).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.mappings(siteId) }),
  })
}

// ---- obligations (feed the critical-risk flag, so mutations invalidate indices) ----
export const useObligations = (id: string) =>
  useQuery({ queryKey: qk.obligations(id), queryFn: () => getJson<Obligation[]>(`/sites/${id}/obligations`), enabled: !!id })

export function useCreateObligation(siteId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: ObligationCreate) => api.post(`/sites/${siteId}/obligations`, body).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.obligations(siteId) })
      qc.invalidateQueries({ queryKey: qk.indices(siteId) })
    },
  })
}

export function useUpdateObligation(siteId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ obligationId, patch }: { obligationId: string; patch: ObligationUpdate }) =>
      api.patch(`/obligations/${obligationId}`, patch).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.obligations(siteId) })
      qc.invalidateQueries({ queryKey: qk.indices(siteId) })
    },
  })
}

export function useDeleteObligation(siteId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (obligationId: string) => api.delete(`/obligations/${obligationId}`).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.obligations(siteId) })
      qc.invalidateQueries({ queryKey: qk.indices(siteId) })
    },
  })
}

/** Confirm/reject a mapping. Confirm auto-answers the question -> recompute. */
export function useReviewMapping(siteId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ mappingId, action }: { mappingId: string; action: 'confirm' | 'reject' }) =>
      api.post(`/mappings/${mappingId}/review`, { action }).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.mappings(siteId) })
      qc.invalidateQueries({ queryKey: qk.files(siteId) })   // file 'to review' count changes
      for (const key of derivedKeys(siteId)) qc.invalidateQueries({ queryKey: key })
    },
  })
}

/**
 * Answering a question recomputes the engine server-side, so on success we
 * invalidate every derived read for that site (indices, barriers, modules,
 * drift, narrative) — the underwriting numbers move without a manual refresh.
 */
export function useSubmitResponse(siteId: string) {
  const qc = useQueryClient()
  return useMutation({
    // The endpoint upserts a list of responses.
    mutationFn: (body: ResponseIn) => api.post(`/sites/${siteId}/responses`, [body]).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.mappings(siteId) })   // answering supersedes its suggestions
      qc.invalidateQueries({ queryKey: qk.files(siteId) })
      for (const key of derivedKeys(siteId)) qc.invalidateQueries({ queryKey: key })
    },
  })
}
